/**
 * How critical a row is: its loss a month on each measure as a share of the site's month on that
 * measure, the site's month being its mean day over the last 12 months × 30, and the higher of the
 * levels (feature_watchdog/issues.md → Criticality). A row with no loss is low.
 */
export const CRITICALITIES = {
    critical: { key: "critical", label: "Critical", from: 10, color: "#D92D20", background: "#FEF3F2", ink: "#B42318" },
    high: { key: "high", label: "High", from: 5, color: "#DC6803", background: "#FFFAEB", ink: "#B54708" },
    medium: { key: "medium", label: "Medium", from: 1, color: "#EAAA08", background: "#FEFBE8", ink: "#A15C07" },
    low: { key: "low", label: "Low", from: 0, color: "#98A2B3", background: "#F2F4F7", ink: "#475467" },
};

/** The site's mean day on `key` over the days it has a value, × 30. */
export const siteMonthlyOf = (siteTraffic, key) => {
    const days = siteTraffic.map((day) => day[key]).filter((value) => value != null);
    return days.length ? days.reduce((total, value) => total + value, 0) / days.length * 30 : 0;
};

/** The row's level, with the share it was read from as `percent`. */
export const criticalityOf = (lostPerMonth, siteMonthly) => {
    const percent = siteMonthly > 0 && lostPerMonth > 0 ? lostPerMonth / siteMonthly * 100 : 0;
    return { ...Object.values(CRITICALITIES).find(({ from }) => percent >= from), percent };
};

/** The highest of a row's levels, one per measure, with the larger share as `percent`; Low with none. */
export const highestOf = (criticalities) => criticalities.reduce((highest, each) => (each.percent > highest.percent ? each : highest), criticalityOf(0, 0));

/** The share as the pill writes it; none when there is no loss. */
export const formatShare = (percent) => {
    if (!(percent > 0)) return null;
    if (percent < 0.1) return "<0.1%";
    return `${percent < 10 ? percent.toFixed(1) : Math.round(percent)}%`;
};

/** What the pill's tooltip says: each measure with a loss, `criticalities` each naming its measure as `name`; none without a loss. */
export const affectsOf = (criticalities) => {
    const [first, second] = criticalities.filter(({ percent }) => formatShare(percent));
    if (!first) return null;
    const firstPart = `This issue affects ${formatShare(first.percent)} of the website's ${first.name}`;
    return second ? `${firstPart} and ${formatShare(second.percent)} of its ${second.name}` : firstPart;
};
